// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents, CustomizerConfirmRemoveEvent } from '@theme/events';
import { replaceViewKeepingFocus } from '@theme/custom.customizer-drill';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */

/**
 * One clip-art design: a small thumbnail url (grid) and the value url stored + drawn on the
 * preview.
 * @typedef {Object} ClipartDesign
 * @property {string} thumb - Small CDN url used for the grid thumbnail.
 * @property {string} value - Larger CDN url; the value pushed to the store and composited onto
 *   the preview.
 * @property {string | null} [alt] - The file's alt text, when the merchant set one.
 */

/**
 * One clip-art category from the `clipart_category` metaobject.
 * @typedef {Object} ClipartCategory
 * @property {string} title
 * @property {ClipartDesign[]} designs
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [viewport] - The container the active view renders into.
 */

/** Designs per page on mobile: one swipeable scroll row. */
const PAGE_SIZE_MOBILE = 12;

/** Minimum horizontal drag (px) that reads as a page swipe rather than a tap. */
const SWIPE_THRESHOLD = 48;

/**
 * Clip-art picker inside a customizer step. Reads the shop-global `clipart_category` metaobjects -
 * server-emitted by snippets/custom.customizer-clipart.liquid as a `[data-clipart-categories]` JSON
 * blob - and renders a two-view gallery: a category list, then the chosen category's designs.
 * The mobile scroll row pages ({@link PAGE_SIZE_MOBILE} at a time, with dots and swipe/drag
 * paging); the desktop grid draws the whole category in one vertical scroller instead.
 *
 * Picking a design pushes its CDN url to the host `<custom-customizer>` store
 * (`store.updateControl(key, url)`); the adapter draws it as a metal-tinted clipart layer on the
 * live preview. REMOVE routes through the shared "Are you sure?" confirm dialog (like every other
 * control), and the confirmed clear resets the selection (`store.updateControl(key, null)`).
 *
 * Mobile keeps only the active page's ~12 thumbnails in the DOM; the desktop scroller holds the
 * whole category, whose thumbs are `loading="lazy"` so the off-screen rows cost no requests.
 * `on:` bindings on the rendered elements are handled by the global delegation in component.js,
 * so dynamically-created nodes need no wiring.
 *
 * @extends Component<Refs>
 */
class CustomizerClipart extends Component {
  /** Parsed category data (empty when the metaobject is absent or not storefront-readable).
   * @type {ClipartCategory[]} */
  #categories = [];

  /** Which view is showing. @type {'categories' | 'designs'} */
  #view = 'categories';

  /** Active category index in the designs view (-1 in the category list). @type {number} */
  #categoryIndex = -1;

  /** Zero-based page within the active category. @type {number} */
  #page = 0;

  /** The selected design value (CDN url), or null. Mirrors the store. @type {string | null} */
  #selected = null;

  /** Cached icon markup (trusted: inlined from theme assets by the snippet).
   * @type {{ back: string }} */
  #icons = { back: '' };

  /** Desktop breakpoint - says whether the designs page at all, mirroring the stylesheet. */
  #desktopQuery = window.matchMedia('(min-width: 1024px)');

  /** Where a swipe gesture started, with the row's scroll-edge state at that moment (null
   * between gestures). @type {{ x: number, y: number, atStart: boolean, atEnd: boolean } | null} */
  #swipeFrom = null;

  /** True right after a horizontal drag: the click that trails the gesture must not pick. */
  #swiped = false;

  connectedCallback() {
    super.connectedCallback();
    this.#categories = this.#parseCategories();
    this.#icons = {
      back: this.querySelector('template[data-clipart-icon="back"]')?.innerHTML ?? '',
    };
    document.addEventListener(ThemeEvents.customizerRemoveConfirmed, this.#handleRemoveConfirmed);
    document.addEventListener(ThemeEvents.customizerUpdate, this.#onStoreUpdate);
    this.#desktopQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.customizerRemoveConfirmed,
      this.#handleRemoveConfirmed
    );
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#onStoreUpdate);
    this.#desktopQuery.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Follow the store's value for this control, so a selection this picker did not make still shows:
   * the wizard starting over after an add (#buildStore in custom.customizer.js), or a hydrated cart
   * edit, must not leave a stale design marked with its Remove pill up.
   *
   * Losing the value also returns the picker to the category list: a drilled view of a cleared
   * selection would be just as stale.
   *
   * Repaints through {@link #rerender}: a store change can arrive mid-click, and rendering
   * synchronously would `replaceChildren` the click target out of the tree before the dialog's
   * click-outside check reads it.
   * @param {Event} event
   */
  #onStoreUpdate = (event) => {
    const { configId } = /** @type {CustomEvent} */ (event).detail;
    if (configId !== this.#configId) return;

    const raw = this.#store?.getControlValue(this.#key);
    const next = typeof raw === 'string' && raw !== '' ? raw : null;
    if (next === this.#selected) return;
    this.#selected = next;

    if (!next) {
      this.#view = 'categories';
      this.#categoryIndex = -1;
      this.#page = 0;
    }
    this.#rerender();
  };

  /** @returns {boolean} True on mobile, where the designs come a page at a time. */
  get #paged() {
    return !this.#desktopQuery.matches;
  }

  /**
   * The breakpoint flipped: the two layouts differ (a page vs the whole category), so repaint the
   * designs view. `#page` is kept, so a trip to desktop and back returns to the shopper's page.
   */
  #handleBreakpointChange = () => {
    if (this.#view === 'designs') this.#render();
  };

  /** @returns {CustomizerStore | undefined} The store held by the host `<custom-customizer>`. */
  get #store() {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    return host?.store;
  }

  /** @returns {string} The control key (also the cart line-item property key). */
  get #key() {
    return this.dataset.controlKey ?? '';
  }

  /** @returns {string} The owning customizer instance's config id (for scoping document events). */
  get #configId() {
    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    return host?.dataset.configId ?? '';
  }

  /** @returns {ClipartCategory[]} Parse the server-emitted JSON blob; never throws. */
  #parseCategories() {
    const script = this.querySelector('script[data-clipart-categories]');
    if (!script?.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  // ---- handlers (leading-slash `on:` bindings target this component) ----

  /**
   * Open a category's designs. The clicked tile carries `data-index`.
   * @param {Event} event
   */
  openCategory(event) {
    const tile = /** @type {Element} */ (event.target).closest('[data-index]');
    if (!(tile instanceof HTMLElement)) return;
    const index = Number(tile.dataset.index);
    if (!Number.isInteger(index) || !this.#categories[index]) return;
    this.#categoryIndex = index;
    this.#page = 0;
    this.#view = 'designs';
    this.#rerender();
  }

  /** Return to the category list. */
  backToCategories() {
    this.#view = 'categories';
    this.#rerender();
  }

  /**
   * Jump to a page within the active category. The clicked dot carries `data-page`.
   * @param {Event} event
   */
  goToPage(event) {
    const dot = /** @type {Element} */ (event.target).closest('[data-page]');
    if (!(dot instanceof HTMLElement)) return;
    const page = Number(dot.dataset.page);
    if (!Number.isInteger(page)) return;
    this.#setPage(page);
  }

  /**
   * Pick a design: mirror the selection and push its url (the cell's `data-value`) to the store.
   * @param {Event} event
   */
  pickDesign(event) {
    // A page swipe that started on a tile still ends in a click on it; that drag must not select.
    if (this.#swiped) {
      this.#swiped = false;
      return;
    }
    const cell = /** @type {Element} */ (event.target).closest('[data-value]');
    if (!(cell instanceof HTMLElement)) return;
    const value = cell.dataset.value ?? null;
    this.#selected = value;
    for (const candidate of this.querySelectorAll('[data-value]')) {
      candidate.setAttribute('aria-checked', String(candidate === cell));
    }
    this.#store?.updateControl(this.#key, value);
    // In-place pill sync (not a re-render): the view must not replay its entrance animation.
    this.#syncHeadRemove();
  }

  /** REMOVE pill opens the confirm dialog (CustomizerConfirmRemoveEvent), not an inline clear:
   * that detaches the pill mid-click, so DialogComponent reads it as outside-click and closes. */
  requestRemove() {
    this.dispatchEvent(new CustomizerConfirmRemoveEvent(this.#configId, this.#key));
  }

  /**
   * The shopper confirmed removal in the shared dialog: clear the selection (and, via the adapter,
   * the preview layer). Scoped to this instance's config id + control key.
   * @param {Event} event
   */
  #handleRemoveConfirmed = (event) => {
    const { configId, controlKey } = /** @type {CustomEvent} */ (event).detail;
    if (controlKey !== this.#key || configId !== this.#configId) return;
    this.#selected = null;
    for (const candidate of this.querySelectorAll('[data-value]')) {
      candidate.setAttribute('aria-checked', 'false');
    }
    this.#store?.updateControl(this.#key, null);
    // In-place pill sync (not a re-render): the view must not replay its entrance animation.
    this.#syncHeadRemove();
  };

  /** Re-renders via requestAnimationFrame, not sync or a microtask: sync detaches the clicked node
   * before it bubbles, closing the dialog; a microtask still fires before the bubble phase too. */
  #rerender() {
    requestAnimationFrame(() => this.#render());
  }

  /** Swap the viewport to the active view. */
  #render() {
    const viewport = this.refs.viewport;
    if (!viewport) return;
    replaceViewKeepingFocus(
      viewport,
      this.#view === 'designs' ? this.#renderDesigns() : this.#renderCategories()
    );
  }

  /**
   * The header row both views share — the SHARED option-head component
   * (snippets/custom.customizer-option-head.liquid; same classes the message/monogram headers
   * render, so one stylesheet drives every consumer). Bold title + the "Select" status, with the
   * shared REMOVE pill (cloned from the `[data-clipart-remove]` template) on the trailing edge
   * once a design is committed — clearing routes through {@link requestRemove}. The title is
   * DATA, not hardcoded copy: the control's metafield label on the category view, the opened
   * category's title on the icon view.
   * @param {string} title
   * @param {{ back?: boolean }} [options] - `back: true` steps back to the categories root
   *   (designs view); otherwise the chevron exits the clipart option to the step's option menu.
   * @returns {HTMLElement}
   */
  #header(title, options = {}) {
    const head = this.#make('div', 'custom-customizer-option-head');
    // Shared breadcrumb (custom.customizer-option-head.liquid), shown on EVERY sub-step per user
    // spec; at the categories root it exits to the step's option menu, mirroring the step Back.
    const backAction = options.back ? '/backToCategories' : 'custom-customizer/prev';
    const back = this.#button('custom-customizer-option-head__back button-unstyled', backAction);
    back.setAttribute('aria-label', this.dataset.labelBack ?? '');
    back.append(this.#icon(this.#icons.back));
    head.append(back);
    const label = this.#make('span', 'custom-customizer-option-head__label');
    const titleEl = document.createElement('strong');
    titleEl.textContent = title;
    label.append(titleEl, ' ');
    const status = this.#make('span', 'custom-customizer-option-head__status');
    status.textContent = this.dataset.labelSelect ?? '';
    label.append(status);
    head.append(label);
    if (this.#selected) head.append(this.#removePill());
    return head;
  }

  /** @returns {DocumentFragment | HTMLElement} A fresh clone of the shared REMOVE pill template. */
  #removePill() {
    const template = this.querySelector('template[data-clipart-remove]');
    return template instanceof HTMLTemplateElement
      ? /** @type {DocumentFragment} */ (template.content.cloneNode(true))
      : this.#make('span', '');
  }

  /** Add/remove the header's REMOVE pill in place after a pick/clear: no full re-render, so the
   * entrance animation doesn't replay on every selection. */
  #syncHeadRemove() {
    const head = this.refs.viewport?.querySelector('.custom-customizer-option-head');
    if (!head) return;
    const pill = head.querySelector('.custom-customizer-control__remove');
    if (this.#selected && !pill) head.append(this.#removePill());
    if (!this.#selected && pill) pill.remove();
  }

  /** @returns {HTMLElement} The category-list view. */
  #renderCategories() {
    const wrap = this.#make('div', 'custom-customizer-clipart__categories');
    wrap.append(this.#header(this.dataset.labelTitle ?? ''));

    // Horizontal scroll row of borderless tiles.
    const row = this.#make('div', 'custom-customizer-clipart__cats');
    this.#categories.forEach((category, index) => {
      const tile = this.#button('custom-customizer-clipart__cat', '/openCategory');
      tile.dataset.index = String(index);
      tile.append(this.#thumbCell(category.designs[0]?.thumb));
      const title = this.#make('span', 'custom-customizer-clipart__cat-title');
      title.textContent = category.title;
      tile.append(title);
      row.append(tile);
    });
    wrap.append(row);
    return wrap;
  }

  /** @returns {HTMLElement} The designs view for the active category. */
  #renderDesigns() {
    const wrap = this.#make('div', 'custom-customizer-clipart__designs');
    const category = this.#categories[this.#categoryIndex];
    if (!category) return wrap;

    // Header: back control + the category title + Select/REMOVE.
    wrap.append(this.#header(category.title, { back: true }));

    const grid = this.#make('div', 'custom-customizer-clipart__grid');
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', category.title);

    // Desktop holds the whole category in one vertical scroller: no pages, no dots, no swipe.
    if (!this.#paged) {
      grid.append(...this.#designCells(category, 0, category.designs.length));
      this.#bindEndFade(wrap, grid);
      wrap.append(grid);
      return wrap;
    }

    const totalPages = Math.max(1, Math.ceil(category.designs.length / PAGE_SIZE_MOBILE));
    this.#page = Math.min(Math.max(this.#page, 0), totalPages - 1);
    const start = this.#page * PAGE_SIZE_MOBILE;
    grid.append(...this.#designCells(category, start, PAGE_SIZE_MOBILE));
    this.#bindSwipe(grid);
    wrap.append(grid);

    if (totalPages > 1) {
      const dots = this.#make('div', 'custom-customizer-clipart__dots');
      const pageLabel = this.dataset.labelPage ?? 'Page';
      for (let page = 0; page < totalPages; page++) {
        const dot = this.#button('custom-customizer-clipart__dot', '/goToPage');
        dot.dataset.page = String(page);
        dot.setAttribute('aria-label', `${pageLabel} ${page + 1}`);
        if (page === this.#page) dot.setAttribute('aria-current', 'true');
        dots.append(dot);
      }
      wrap.append(dots);
    }

    return wrap;
  }

  /**
   * Design tiles: a page's worth on mobile, the whole category on desktop.
   * @param {ClipartCategory} category
   * @param {number} start - Index of the first design to draw.
   * @param {number} count - How many to draw from there.
   * @returns {HTMLElement[]}
   */
  #designCells(category, start, count) {
    const template = this.dataset.labelDesign ?? '[category] [number]';
    return category.designs.slice(start, start + count).map((design, index) => {
      const cell = this.#button('custom-customizer-clipart__item', '/pickDesign');
      cell.dataset.value = design.value;
      cell.setAttribute('role', 'radio');
      // The thumbnail is all a design shows, so without a name every tile reads "radio button".
      const label =
        design.alt ||
        template
          .replace('[category]', () => category.title)
          .replace('[number]', String(start + index + 1));
      cell.setAttribute('aria-label', label);
      cell.setAttribute('aria-checked', String(design.value === this.#selected));
      // Shared selected treatment (accent border + corner check) — the "Option cards" block in
      // snippets/custom.customizer-step.liquid keys on this attribute.
      cell.setAttribute('data-option-card', 'tile');
      cell.append(this.#thumbCell(design.thumb));
      return cell;
    });
  }

  /**
   * Change page (clamped) and swap the grid's tiles IN PLACE — grid children + dot states only,
   * never the whole view, so the entrance animation doesn't replay on every page hop. Deferred a
   * frame for the same detached-node reason as {@link #rerender}.
   * @param {number} page
   * @param {{ fromEnd?: boolean }} [options] - Land the mobile scroll row at its far end
   *   (a backward swipe reads as the previous page's tail sliding back in).
   */
  #setPage(page, options = {}) {
    const category = this.#categories[this.#categoryIndex];
    if (!category) return;
    const totalPages = Math.max(1, Math.ceil(category.designs.length / PAGE_SIZE_MOBILE));
    const clamped = Math.min(Math.max(page, 0), totalPages - 1);
    if (clamped === this.#page) return;
    this.#page = clamped;
    requestAnimationFrame(() => this.#updatePage(options.fromEnd === true));
  }

  /**
   * Apply {@link #setPage}'s page to the live designs view: re-fill the grid, sync the dots,
   * reset the mobile row's scroll position. Falls back to a full render if the grid is gone
   * (e.g. the view switched during the deferred frame).
   * @param {boolean} fromEnd
   */
  #updatePage(fromEnd) {
    const category = this.#categories[this.#categoryIndex];
    if (!category || this.#view !== 'designs') return;
    const grid = this.refs.viewport?.querySelector('.custom-customizer-clipart__grid');
    if (!(grid instanceof HTMLElement)) {
      this.#render();
      return;
    }
    const start = this.#page * PAGE_SIZE_MOBILE;
    grid.replaceChildren(...this.#designCells(category, start, PAGE_SIZE_MOBILE));
    grid.scrollLeft = fromEnd ? grid.scrollWidth : 0;
    // Slide the new tiles in so the page turn is visible. Clear and re-add the class (with a
    // reflow between) so a second hop replays the animation.
    const slide = fromEnd
      ? 'custom-customizer-clipart__grid--from-start'
      : 'custom-customizer-clipart__grid--from-end';
    grid.classList.remove(
      'custom-customizer-clipart__grid--from-start',
      'custom-customizer-clipart__grid--from-end'
    );
    void grid.offsetWidth;
    grid.classList.add(slide);
    for (const dot of this.refs.viewport?.querySelectorAll('[data-page]') ?? []) {
      if (Number(dot.getAttribute('data-page')) === this.#page) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  /**
   * The desktop scroller fades its trailing row, so the list reads as continuing past the footer.
   * The fade has to lift once the scroller is at its end, or the last row sits permanently
   * half-faded. The listener dies with the grid, which every render replaces.
   * @param {HTMLElement} wrap - The designs view; carries the state class the fade keys on.
   * @param {HTMLElement} grid
   */
  #bindEndFade(wrap, grid) {
    const sync = () => {
      const atEnd = grid.scrollTop >= grid.scrollHeight - grid.clientHeight - 1;
      wrap.classList.toggle('custom-customizer-clipart__designs--at-end', atEnd);
    };
    grid.addEventListener('scroll', sync, { passive: true });
    // Layout is a frame out at render time: an unlaid-out grid measures as "at end" and would
    // paint the fade away on a list that does scroll.
    requestAnimationFrame(sync);
  }

  /**
   * Mobile only: let a horizontal drag/swipe page the designs, alongside the dots. Pointer events
   * cover mouse/pen; fingers use the touch pair because native scrolling cancels a touch-derived
   * pointer stream (pointercancel — pointerup then never arrives), while touchend always fires.
   * Paging acts only on the gesture's END, so native panning inside the row keeps working: a
   * swipe only pages when the row was already at the matching scroll edge when the finger came
   * down.
   *
   * A MOUSE gets no native panning at all - a row only a finger can move looks stuck under a
   * cursor - so a mouse/pen drag scrolls the row itself, and the same gesture still pages at the
   * edges.
   * @param {HTMLElement} grid
   */
  #bindSwipe(grid) {
    grid.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      this.#beginSwipe(grid, event.clientX, event.clientY);
      // Show `grabbing` on the whole picker during a drag (tiles otherwise show `pointer`). Mouse
      // and pen only: a finger has no cursor, and an unended touch would strand the class.
      this.classList.add('custom-customizer-clipart--dragging');
      const fromScroll = grid.scrollLeft;
      const track = (/** @type {PointerEvent} */ move) => {
        if (move.pointerId !== event.pointerId) return;
        grid.scrollLeft = fromScroll - (move.clientX - event.clientX);
      };
      // A flick's release usually lands OUTSIDE the grid, so settle at the window, keyed to this
      // pointerId: a stray release must not strand #swipeFrom and pair with an unrelated press.
      const settle = (/** @type {PointerEvent} */ up) => {
        if (up.pointerId !== event.pointerId) return;
        window.removeEventListener('pointermove', track);
        window.removeEventListener('pointerup', settle);
        window.removeEventListener('pointercancel', settle);
        if (up.type === 'pointerup') this.#endSwipe(up.clientX, up.clientY);
        else {
          this.#swipeFrom = null;
          this.classList.remove('custom-customizer-clipart--dragging');
        }
      };
      window.addEventListener('pointermove', track);
      window.addEventListener('pointerup', settle);
      window.addEventListener('pointercancel', settle);
    });
    grid.addEventListener(
      'touchstart',
      (event) => {
        // A second finger (pinch/zoom) is not a swipe — abort rather than pair mismatched points.
        if (event.touches.length > 1) {
          this.#swipeFrom = null;
          return;
        }
        const touch = event.changedTouches.item(0);
        if (touch) this.#beginSwipe(grid, touch.clientX, touch.clientY);
      },
      { passive: true }
    );
    grid.addEventListener(
      'touchend',
      (event) => {
        // Only the LAST finger lifting ends the gesture.
        if (event.touches.length > 0) return;
        const touch = event.changedTouches.item(0);
        if (touch) this.#endSwipe(touch.clientX, touch.clientY);
      },
      { passive: true }
    );
  }

  /**
   * @param {HTMLElement} grid
   * @param {number} x
   * @param {number} y
   */
  #beginSwipe(grid, x, y) {
    this.#swiped = false;
    const maxScroll = grid.scrollWidth - grid.clientWidth;
    this.#swipeFrom = {
      x,
      y,
      atStart: grid.scrollLeft <= 1,
      atEnd: grid.scrollLeft >= maxScroll - 1,
    };
  }

  /**
   * @param {number} x
   * @param {number} y
   */
  #endSwipe(x, y) {
    this.classList.remove('custom-customizer-clipart--dragging');
    const from = this.#swipeFrom;
    this.#swipeFrom = null;
    if (!from) return;
    const dx = x - from.x;
    const dy = y - from.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    // Even at the first/last page the drag's trailing click must not read as a pick; clear on the
    // next frame, so a swipe whose click never arrives cannot swallow a later AT activation.
    this.#swiped = true;
    requestAnimationFrame(() => {
      this.#swiped = false;
    });
    if (dx < 0 && from.atEnd) this.#setPage(this.#page + 1);
    else if (dx > 0 && from.atStart) this.#setPage(this.#page - 1, { fromEnd: true });
  }

  /**
   * @param {string} tag
   * @param {string} className
   * @returns {HTMLElement}
   */
  #make(tag, className) {
    const node = document.createElement(tag);
    node.className = className;
    return node;
  }

  /**
   * A `<button type="button">` with a declarative click binding.
   * @param {string} className
   * @param {string} onClick - The `on:click` value (leading-slash targets this component).
   * @returns {HTMLButtonElement}
   */
  #button(className, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('on:click', onClick);
    return button;
  }

  /**
   * A fixed-size cell holding a (contained) thumbnail image.
   * @param {string} [thumb]
   * @returns {HTMLElement}
   */
  #thumbCell(thumb) {
    const cell = this.#make('span', 'custom-customizer-clipart__cell');
    if (thumb) {
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 44;
      img.height = 44;
      // Native image drag would swallow a mouse page-swipe mid-gesture (no pointerup ever fires).
      img.draggable = false;
      // The loaded flag lets custom.customizer-clipart.liquid fade each thumb in (that rule is
      // reduced-motion-gated). `error` reveals too: a broken thumb must show as an empty cell.
      const reveal = () => img.classList.add('custom-customizer-clipart__thumb--loaded');
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
      img.src = thumb;
      if (img.complete) reveal();
      cell.append(img);
    }
    return cell;
  }

  /**
   * A `.svg-wrapper` span holding trusted inline-SVG markup from theme assets.
   * @param {string} markup
   * @returns {HTMLElement}
   */
  #icon(markup) {
    const wrapper = this.#make('span', 'svg-wrapper');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.innerHTML = markup;
    return wrapper;
  }
}

if (!customElements.get('custom-customizer-clipart')) {
  customElements.define('custom-customizer-clipart', CustomizerClipart);
}
